import React, { useEffect } from 'react'
import './Aboutkaramale.css'
import { Link } from 'react-router-dom'
import Header5 from './components/Header5'
import Header from './layouts/Header'
import {
  DashboardMock,
  CreateEventMock,
  ListedEventMock,
  RevenueMock,
  SalesMock,
  PerformanceMock,
} from './AboutMockups'

const PLAY_URL = 'https://play.google.com/store/apps/details?id=com.karamalescanner&hl=en'

const HERO_BARS = [32, 46, 39, 58, 51, 72, 65, 84, 76, 94]
const AUDIENCE = ['Shows', 'Conferences', 'Parties', 'Festivals', 'Gatherings']

const STEPS = [
  ['1', 'Sign up', 'Create your organiser account and tell us a little about yourself.'],
  ['2', 'Verify', 'Confirm your account using the secure code sent to your email.'],
  ['3', 'Launch', 'Create your event, set your tickets and start selling to your audience.'],
]

const DASH_FEATURES = [
  'Create unlimited events',
  'Set your own ticket prices',
  'Track sales and payouts',
]

const MARKETING = [
  ['Your contacts', 'Reach people already connected to your event through direct communication.'],
  [
    'Karamale audience',
    'Put your event where people can discover new experiences and upcoming events.',
  ],
  [
    'Your lists',
    'Bring your own audience and communicate with the people who matter most to your event.',
  ],
]

const SHOWCASE = [
  [<DashboardMock key="d" />, 'Dashboard', 'Your event command centre.'],
  [<CreateEventMock key="c" />, 'Create', 'Build your event.'],
  [<ListedEventMock key="l" />, 'Launch', 'Get your event listed.'],
]

const ANALYTICS = [
  [<RevenueMock key="r" />, 'Revenue', 'See what your event is earning.'],
  [<SalesMock key="s" />, 'Sales progress', 'Understand how ticket sales are moving.'],
  [<PerformanceMock key="p" />, 'Performance', 'Measure the bigger picture.'],
]

const INCLUDED = [
  'Create unlimited events',
  'Publish whenever you are ready',
  'Real-time sales and payouts',
  'Ticket admissions and attendance',
  'Promoter accounts and sales tracking',
  'Scanner app for the door',
]

const PROMOTER_POINTS = [
  'Individual accounts',
  'Sales by promoter',
  'Income and ticket categories',
  'Deactivate access anytime',
]

const PROMOTERS = [
  ['TM', 'Thabo M.', 'VIP / General', 248],
  ['LN', 'Lerato N.', 'General', 193],
  ['SK', 'Sipho K.', 'VIP', 117],
]

const SCAN_POINTS = [
  'Every scan is tracked in real time.',
  'Invalid or already-scanned tickets are rejected.',
  'See attendance, no-shows and ticket categories.',
  'Support both physical and digital tickets.',
]

const Aboutkaramale = () => {
  useEffect(() => {
    document.title = 'Karamale - About Karamale'
    window.scrollTo(0, 0)
  }, [])

  const isPartner = localStorage.getItem('partnerid') !== null

  return (
    <div className="ak-page">
      {isPartner ? <Header5 /> : <Header />}

      {/* HERO */}
      <section className="ak-hero">
        <div className="ak-aurora" aria-hidden="true">
          <span className="ak-aurora__a" />
          <span className="ak-aurora__b" />
        </div>
        <div className="ak-wrap ak-hero__inner">
          <div>
            <span className="ak-pill">
              <i /> The event platform
            </span>
            <h1>
              Selling tickets
              <br />
              <em>made easy.</em>
            </h1>
            <p className="ak-hero__sub">
              Create, launch and manage your events from one simple platform. Sell tickets, reach
              your audience and keep your event moving.
            </p>
            <div className="ak-actions">
              <Link to="/organiser-registration" className="ak-btn ak-btn--brand">
                Start selling
              </Link>
              <a href="#how-it-works" className="ak-btn ak-btn--ghost">
                See how it works
              </a>
            </div>
          </div>

          <div className="ak-hero__visual">
            <div className="ak-dash">
              <div className="ak-dash__top">
                <span>Organiser dashboard</span>
                <span className="ak-live">
                  <i /> Live
                </span>
              </div>
              <div className="ak-dash__stat">
                <small>Ticket revenue</small>
                <strong>R48,620</strong>
                <b>+18.4% this month</b>
              </div>
              <div className="ak-dash__chart">
                {HERO_BARS.map((h, i) => (
                  <span key={i} style={{ height: `${h}%` }} className={i === 9 ? 'is-hot' : ''} />
                ))}
              </div>
              <div className="ak-dash__foot">
                {[
                  ['Tickets sold', '1,248'],
                  ['Events', '12'],
                  ['Attendance', '86%'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <small>{k}</small>
                    <strong>{v}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div className="ak-float ak-float--one">
              <small>Tickets sold</small>
              <strong>+248</strong>
            </div>
            <div className="ak-float ak-float--two">
              <small>Event status</small>
              <strong>
                <i /> Active
              </strong>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="ak-section">
        <div className="ak-wrap ak-two">
          <h2 className="ak-h2">
            Built to put <span>organisers in control.</span>
          </h2>
          <div className="ak-card">
            <p className="ak-lead">
              Karamale is a smart, user-friendly ticketing platform that gives event organisers the
              tools they need to create, launch and manage events while selling tickets online and
              offline.
            </p>
            <p>
              From professional event planners and promoters to independent organisers, Karamale is
              designed for anyone who wants a simpler way to get an event from an idea to a live
              audience.
            </p>
            <div className="ak-tags">
              {AUDIENCE.map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="ak-section" id="how-it-works">
        <div className="ak-wrap">
          <div className="ak-head">
            <h2 className="ak-h2">
              From idea to <span>live event.</span>
            </h2>
            <p>
              Getting started takes only a few steps. Once your account is verified, your organiser
              dashboard puts everything you need in one place.
            </p>
          </div>
          <ol className="ak-steps">
            {STEPS.map(([n, title, text]) => (
              <li key={n} className="ak-step">
                <span className="ak-step__n">{n}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* DASHBOARD */}
      <section className="ak-section">
        <div className="ak-wrap ak-two">
          <div>
            <span className="ak-pill">
              <i /> Your command centre
            </span>
            <h2 className="ak-h2 ak-h2--mt">
              Everything your <span>event needs.</span>
            </h2>
            <p className="ak-lead">
              Your organiser dashboard brings your event, tickets, sales and payouts together so you
              can focus on running the experience rather than chasing the numbers.
            </p>
            <ul className="ak-checks">
              {DASH_FEATURES.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
          <DashboardMock />
        </div>
      </section>

      {/* MARKETING */}
      <section className="ak-section">
        <div className="ak-wrap">
          <div className="ak-head">
            <h2 className="ak-h2">
              Your event deserves <span>an audience.</span>
            </h2>
            <p>
              Karamale gives organisers more than ticketing. Connect with your existing audience
              while putting your event in front of people already looking for something to do.
            </p>
          </div>
          <div className="ak-cols">
            {MARKETING.map(([title, text], i) => (
              <div key={title} className={`ak-card ${i === 1 ? 'ak-card--hot' : ''}`}>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="ak-section">
        <div className="ak-wrap">
          <h2 className="ak-h2 ak-h2--solo">
            From creating your event to seeing it <span>go live.</span>
          </h2>
          <div className="ak-cols">
            {SHOWCASE.map(([mock, tag, title]) => (
              <article key={tag} className="ak-feature">
                {mock}
                <small>{tag}</small>
                <h3>{title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ANALYTICS */}
      <section className="ak-section">
        <div className="ak-wrap">
          <div className="ak-head">
            <h2 className="ak-h2">
              Know what's <span>happening.</span>
            </h2>
            <p>
              Keep an eye on performance as your event grows. Karamale gives you visibility into
              revenue, sales progress and overall event performance.
            </p>
          </div>
          <div className="ak-cols">
            {ANALYTICS.map(([mock, tag, title]) => (
              <article key={tag} className="ak-feature">
                {mock}
                <small>{tag}</small>
                <h3>{title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="ak-section">
        <div className="ak-wrap">
          <div className="ak-head">
            <h2 className="ak-h2">
              Free to publish. <span>You only pay when you sell.</span>
            </h2>
            <p>
              Creating and publishing events on Karamale is free. There is no budget to fund up
              front. Karamale takes 3.5% on every ticket sold, and that's it.
            </p>
          </div>
          <div className="ak-two ak-two--top">
            <div className="ak-fee">
              <small>Service fee</small>
              <strong>3.5%</strong>
              <p>
                Taken from each ticket sold. No upfront payment and no hidden fees, so you only pay
                when your event makes money.
              </p>
              <div className="ak-fee__points">
                {[
                  ['R0', 'Create and publish'],
                  ['3.5%', 'Per ticket sold'],
                  ['No', 'Upfront budget'],
                ].map(([k, v]) => (
                  <div key={v}>
                    <b>{k}</b>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="ak-card">
              <h3>What's included</h3>
              <ul className="ak-checks">
                {INCLUDED.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PROMOTERS */}
      <section className="ak-section">
        <div className="ak-wrap ak-two">
          <div>
            <span className="ak-pill">
              <i /> Built for promoters
            </span>
            <h2 className="ak-h2 ak-h2--mt">
              Give your promoters <span>their own lane.</span>
            </h2>
            <p className="ak-lead">
              Create unique promoter accounts, assign access and see exactly how each promoter is
              performing.
            </p>
            <ul className="ak-checks">
              {PROMOTER_POINTS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
          <div className="ak-panel">
            <div className="ak-panel__head">
              <span>Promoter performance</span>
              <span>This event</span>
            </div>
            {PROMOTERS.map(([init, name, cat, n]) => (
              <div key={name} className="ak-prow">
                <span className="ak-person">{init}</span>
                <div>
                  <strong>{name}</strong>
                  <small>{cat}</small>
                </div>
                <b>{n}</b>
              </div>
            ))}
            <div className="ak-ptotal">
              <span>Total promoter sales</span>
              <strong>558</strong>
            </div>
          </div>
        </div>
      </section>

      {/* SCANNING */}
      <section className="ak-section">
        <div className="ak-wrap ak-two">
          <div className="ak-scan">
            <div className="ak-phone" aria-hidden="true">
              <div className="ak-phone__speaker" />
              <div className="ak-phone__screen">
                <b className="ak-brand">
                  karamale<i>.</i>
                </b>
                <div className="ak-frame">
                  <i className="tl" />
                  <i className="tr" />
                  <i className="bl" />
                  <i className="br" />
                  <span className="ak-frame__line" />
                </div>
                <strong>Scan ticket</strong>
                <small>Position the QR code inside the frame</small>
              </div>
            </div>
          </div>
          <div>
            <span className="ak-pill">
              <i /> Ticket scanning
            </span>
            <h2 className="ak-h2 ak-h2--mt">
              Your phone becomes <span>your scanner.</span>
            </h2>
            <p className="ak-lead">
              Karamale's dedicated scanning experience lets your team validate tickets at the door
              without expensive specialist hardware.
            </p>
            <ul className="ak-checks">
              {SCAN_POINTS.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
            <p className="ak-note">
              <b>Digital tickets</b> capture attendee information such as name, email and phone for
              deeper event insights.
            </p>
            <a
              href={PLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ak-play"
              aria-label="Download the Kscanner app on Google Play"
            >
              <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
                <path
                  fill="#00d6ff"
                  d="M3.6 2.3c-.3.3-.5.8-.5 1.4v16.6c0 .6.2 1.1.5 1.4l.1.1 9.3-9.3v-.2L3.7 2.2z"
                />
                <path
                  fill="#ffce00"
                  d="M16.1 15.4l-3.1-3.1v-.2l3.1-3.1.1.1 3.7 2.1c1 .6 1 1.6 0 2.2l-3.7 2.1z"
                />
                <path fill="#ff3a44" d="M16.2 15.3L13 12.2 3.6 21.6c.3.4.9.4 1.5.1l11.1-6.4" />
                <path fill="#00f076" d="M16.2 9L5.1 2.7c-.6-.3-1.2-.3-1.5.1L13 12.2z" />
              </svg>
              <span>
                <small>Get it on Google Play</small>
                Download Kscanner app
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ak-section ak-section--last">
        <div className="ak-wrap">
          <div className="ak-cta">
            <b className="ak-brand ak-brand--light">
              karamale<i>.</i>
            </b>
            <h2>
              Create. Launch. <em>Sell.</em>
            </h2>
            <p>Easy and simple. Ten minutes is all you need to get your event moving.</p>
            <Link to="/organiser-registration" className="ak-btn ak-btn--light">
              Create your event
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Aboutkaramale
